import React, { useState, useRef } from 'react';
import { apiPost, apiPostLongRunning, secureFileUpload } from './apiUtils';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './components/ui/card';
import { Button } from './components/ui/button';
import { Badge } from './components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './components/ui/table';
import { Alert, AlertDescription } from './components/ui/alert';
import { Upload, FileCheck, ExternalLink, CheckCircle, Info } from 'lucide-react';
import useBusinessDate from './hooks/useBusinessDate';
import { formatSize, formatCurrency } from './utils/formatters';
import { toast } from 'sonner';
import { useNavigation } from './contexts/NavigationContext';

function UploadInvoice() {
  const { navigate } = useNavigation();
  const businessDate = useBusinessDate();
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [parsedData, setParsedData] = useState(null);
  const [processing, setProcessing] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    
    if (file) {
      // Check if it's a PDF file
      const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
      
      if (isPdf) {
        setSelectedFile(file);
        setParsedData(null);
      } else {
        toast.error('Please select a PDF file');
        // Clear the input
        e.target.value = '';
      }
    }
  };

  const handleFileButtonClick = () => {
    // Try useRef approach first
    if (fileInputRef.current) {
      fileInputRef.current.click();
      return;
    }
    
    // Fallback to getElementById
    const fileInput = document.getElementById('file-input');
    if (fileInput) {
      fileInput.click();
    }
  };



  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error('Please select a file first');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('invoice', selectedFile);

    try {
      const response = await secureFileUpload('/api/invoice/upload', formData);

      if (response.ok) {
        const data = await response.json();
        setParsedData(data);
        toast.success(`Invoice processed successfully! Found ${data.items.length} items.`);
      } else {
        const error = await response.json();
        toast.error(`Error: ${error.message}`);
      }
    } catch (error) {
      console.error('Error uploading invoice:', error);
      toast.error('Error uploading invoice');
    }
    setUploading(false);
  };

  const handleConfirmAndAdd = async () => {
    if (!parsedData || !parsedData.tempId) return;

    setProcessing(true);
    try {
      console.log('🗓️ Using business date:', businessDate);
      
      // Show processing message for large invoices
      const itemCount = parsedData.items?.length || 0;
      if (itemCount > 50) {
        console.log(`📦 Processing large invoice with ${itemCount} items - this may take up to 60 seconds...`);
      }
      
      const response = await apiPostLongRunning('/api/invoice/confirm', {
        tempId: parsedData.tempId,
        businessDate: businessDate
      });
      
      if (response.ok) {
        const result = await response.json();
        const processingTime = result.processingTime ? ` (${Math.round(result.processingTime / 1000)}s)` : '';
        toast.success(`Invoice confirmed successfully!${processingTime}\nInvoice: ${result.invoiceNumber}\nValue: ₹${result.totalValue}\nItems: ${result.matchedItemsCount} matched`, {
          duration: 5000
        });
        
        // Reset form
        setSelectedFile(null);
        setParsedData(null);
        
        // Navigate to view stock
        navigate('manageStock');
      } else {
        // Handle HTTP error responses
        const errorData = await response.json();
        console.error('Invoice confirmation failed:', errorData);
        
        // Handle specific error cases
        if (errorData.code === 'INVOICE_EXPIRED') {
          toast.error('Invoice data expired. Please upload the invoice again.\n\nThis happens when more than 30 minutes pass between upload and confirmation.', {
            duration: 6000
          });
        } else if (errorData.code === 'INVOICE_ALREADY_PROCESSED') {
          toast.error('This invoice has already been processed.\n\nEach invoice can only be confirmed once per shop.', {
            duration: 5000
          });
        } else {
          toast.error(`Invoice confirmation failed: ${errorData.message || `Server error (${response.status})`}`);
        }
      }
    } catch (error) {
      console.error('Error confirming invoice:', error);
      
      // Handle timeout specifically
      if (error.message.includes('timeout')) {
        toast.error('Invoice processing timed out.\n\nThis can happen with large invoices. Please try again.\n\nIf the problem persists, the invoice may have been processed successfully - check your stock records.', {
          duration: 7000
        });
      } else {
        toast.error(`Error processing invoice: ${error.message || 'Network error'}`);
      }
    }
    setProcessing(false);
  };

  const handleCancel = async () => {
    if (!parsedData || !parsedData.tempId) return;

    try {
      await apiPost('/api/invoice/cancel', {
        tempId: parsedData.tempId
      });
      console.log('Invoice cancelled successfully');
      toast.info('Upload cancelled');
    } catch (error) {
      console.warn('Error cancelling invoice:', error);
      // Still reset the form even if API call fails
      toast.info('Upload cancelled');
    }

    // Reset form state
    setSelectedFile(null);
    setParsedData(null);
  };

  return (
    <main className="w-full px-4 py-8 space-y-8 max-w-7xl mx-auto">
        {/* Header Section */}
        <div className="space-y-4 text-left">
          <div className="space-y-2">
            <h1 className="text-4xl font-bold text-left">Upload Invoice</h1>
            <p className="text-base text-muted-foreground text-left">Upload government invoice PDF to automatically update received stock</p>
          </div>

          {/* Excise Portal Link Card */}
          <Alert className="bg-blue-50 border-blue-200">
            <ExternalLink className="h-5 w-5 text-blue-600" />
            <AlertDescription className="ml-2 text-left">
              <p className="font-medium text-blue-900 mb-2 text-left">📄 Need to download your invoice PDF?</p>
              <a 
                href="https://tgbcl.telangana.gov.in/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-800 hover:underline font-medium text-left block"
              >
                Visit Telangana Excise Portal to download your invoice PDF →
              </a>
              <p className="text-xs text-muted-foreground mt-2 text-left">
                Download your invoice PDF from the portal and come back here to upload it
              </p>
            </AlertDescription>
          </Alert>
        </div>

        {/* Upload Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-left">
              {selectedFile ? <FileCheck className="h-6 w-6 text-green-600" /> : <Upload className="h-6 w-6" />}
              {selectedFile ? 'File Selected' : 'Select Invoice PDF'}
            </CardTitle>
            {!selectedFile && (
              <CardDescription className="text-left">PDF files only, max 5MB</CardDescription>
            )}
          </CardHeader>
          <CardContent className="space-y-6">
            <input
              type="file"
              id="file-input"
              ref={fileInputRef}
              accept=".pdf,application/pdf"
              onChange={handleFileSelect}
              className="hidden"
            />

            <div 
              onClick={!selectedFile ? handleFileButtonClick : undefined}
              className={`flex flex-col items-center justify-center p-8 border-2 border-dashed rounded-lg transition-all ${
                selectedFile 
                  ? 'border-green-500 bg-green-50' 
                  : 'border-border hover:border-primary hover:bg-accent cursor-pointer'
              }`}
            >
              {!selectedFile ? (
                <div className="text-center space-y-4">
                  <div className="mx-auto h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center">
                    <Upload className="h-8 w-8 text-primary" />
                  </div>
                  <div className="space-y-2">
                    <p className="text-sm font-medium">Click to choose PDF file</p>
                    <p className="text-xs text-muted-foreground">
                      Upload your government invoice to automatically read and add to received stock
                    </p>
                  </div>
                </div>
              ) : (
                <div className="text-center space-y-4">
                  <div className="mx-auto h-16 w-16 rounded-full bg-green-100 flex items-center justify-center">
                    <FileCheck className="h-8 w-8 text-green-600" />
                  </div>
                  <div className="space-y-2">
                    <p className="font-semibold">{selectedFile.name}</p>
                    <p className="text-sm text-muted-foreground">
                      Size: {(selectedFile.size / 1024).toFixed(2)} KB
                    </p>
                    <Button 
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleFileButtonClick();
                      }}
                      className="mt-2"
                    >
                      Change File
                    </Button>
                  </div>
                </div>
              )}
            </div>

            {selectedFile && (
              <Button 
                className="w-full"
                size="lg"
                onClick={handleUpload}
                disabled={uploading}
              >
                {uploading ? 'Reading Invoice...' : 'Read Invoice'}
              </Button>
            )}
          </CardContent>
        </Card>

        {/* Parsed Invoice Data Section */}
        {parsedData && (
          <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-left">
                  <CheckCircle className="h-6 w-6 text-green-600" />
                  Parsed Invoice Data
                </CardTitle>
                <CardDescription className="text-left">Review the extracted information before confirming</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Invoice Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-muted rounded-lg">
                  <div className="text-left">
                    <p className="text-sm font-medium text-muted-foreground text-left">Invoice Number</p>
                    <p className="text-lg font-bold text-left">{parsedData.invoiceNumber}</p>
                  </div>
                  <div className="text-left">
                    <p className="text-sm font-medium text-muted-foreground text-left">Date</p>
                    <p className="text-lg font-bold text-left">{parsedData.date}</p>
                  </div>
                </div>

                {/* Financial Breakdown */}
                <div className="space-y-3 p-4 border rounded-lg">
                  <h4 className="font-semibold text-lg mb-3 text-left">Financial Breakdown</h4>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <span className="text-muted-foreground">Invoice Value:</span>
                    <span className="font-semibold text-right">{formatCurrency(parsedData.invoiceValue)}</span>
                    
                    <span className="text-muted-foreground">MRP Rounding Off:</span>
                    <span className="font-semibold text-right">{formatCurrency(parsedData.mrpRoundingOff)}</span>
                    
                    <span className="text-muted-foreground">Excise Turnover Tax:</span>
                    <span className="font-semibold text-right">{formatCurrency(parsedData.retailExciseTurnoverTax)}</span>
                    
                    <span className="text-muted-foreground">Special Excise Cess:</span>
                    <span className="font-semibold text-right">{formatCurrency(parsedData.specialExciseCess)}</span>
                    
                    <span className="text-muted-foreground">TCS:</span>
                    <span className="font-semibold text-right">{formatCurrency(parsedData.tcs)}</span>
                    
                    <div className="col-span-2 border-t pt-3 mt-3">
                      <div className="grid grid-cols-2">
                        <span className="font-bold">Total Purchase Value:</span>
                        <span className="font-bold text-right text-primary text-lg">{formatCurrency(parsedData.totalAmount)}</span>
                      </div>
                    </div>
                  </div>
                </div>

              {/* Items Table */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h4 className="font-semibold text-lg text-left">Items Found</h4>
                  <Badge variant="secondary">{parsedData.items.length} items</Badge>
                </div>
                
                <div className="rounded-md border overflow-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[60px] text-center">S.No</TableHead>
                        <TableHead className="text-center">Brand Number</TableHead>
                        <TableHead className="text-center">Description</TableHead>
                        <TableHead className="text-center">Size</TableHead>
                        <TableHead className="text-center">Cases</TableHead>
                        <TableHead className="text-center">Bottles</TableHead>
                        <TableHead className="text-center">Total Qty</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {parsedData.items.map((item, index) => (
                        <TableRow key={index}>
                          <TableCell className="font-medium text-center">{item.serial || (index + 1)}</TableCell>
                          <TableCell className="font-medium text-center">{item.brandNumber}</TableCell>
                          <TableCell className="text-center">{item.description}</TableCell>
                          <TableCell className="text-center">{formatSize(item.sizeCode, item.size)}</TableCell>
                          <TableCell className="text-center">{item.cases}</TableCell>
                          <TableCell className="text-center">{item.bottles}</TableCell>
                          <TableCell className="text-center font-semibold">{item.totalQuantity}</TableCell>
                        </TableRow>
                      ))}
                      {/* Totals Row */}
                      <TableRow className="bg-muted font-bold">
                        <TableCell colSpan={4} className="text-right">TOTAL</TableCell>
                        <TableCell className="text-center">{parsedData.items.reduce((sum, item) => sum + (item.cases || 0), 0)}</TableCell>
                        <TableCell className="text-center">{parsedData.items.reduce((sum, item) => sum + (item.bottles || 0), 0)}</TableCell>
                        <TableCell className="text-center">{parsedData.items.reduce((sum, item) => sum + (item.totalQuantity || 0), 0)}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-4 pt-4">
                <Button 
                  variant="outline"
                  className="flex-1"
                  onClick={handleCancel}
                >
                  Cancel
                </Button>
                <Button 
                  className="flex-1"
                  onClick={handleConfirmAndAdd}
                  disabled={processing}
                >
                  {processing ? 
                    (parsedData?.items?.length > 50 ? 
                      'Processing (may take up to 60s)...' : 
                      'Processing...'
                    ) : 
                    'Confirm & Add to Stock'
                  }
                </Button>
              </div>
              </CardContent>
            </Card>
          )}

        {/* Info Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-left">
                <Info className="h-5 w-5" />
                Supported Format
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-4 text-left">Government invoice PDFs with standard format including:</p>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-primary mt-0.5">•</span>
                  <span>Brand numbers with pack quantities (e.g., 0110 (48))</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary mt-0.5">•</span>
                  <span>Package sizes (180ml, 375ml, 750ml, etc.)</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary mt-0.5">•</span>
                  <span>Case and bottle quantities</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary mt-0.5">•</span>
                  <span>All applicable taxes and charges</span>
                </li>
              </ul>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-left">
                <CheckCircle className="h-5 w-5" />
                Auto-Processing
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-4 text-left">The system will automatically:</p>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-green-600 mt-0.5">✓</span>
                  <span>Parse PDF and extract product details</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-600 mt-0.5">✓</span>
                  <span>Match products with master brands</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-600 mt-0.5">✓</span>
                  <span>Calculate total quantities</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-600 mt-0.5">✓</span>
                  <span>Update received stock in daily records</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-green-600 mt-0.5">✓</span>
                  <span>Calculate total purchase value with taxes</span>
                </li>
              </ul>
            </CardContent>
          </Card>
        </div>
      </main>
  );
}

export default UploadInvoice;